import api from "@/lib/api";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Mail, Loader2, Eye, ChevronLeft, ChevronRight } from "lucide-react";

const ALL_STORES = "__all__";

function ReportCard({ title, description, format, onDownload, testid }) {
  const Icon = format === "PDF" ? FileText : FileSpreadsheet;
  return (
    <div className="grid-border-card p-6 flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <Icon className="w-8 h-8 text-accent" strokeWidth={1.25}/>
        <span className="overline">{format}</span>
      </div>
      <div>
        <h3 className="font-display text-xl">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1.5">{description}</p>
      </div>
      <Button onClick={onDownload} variant="outline" className="w-full" data-testid={testid}>
        <Download className="w-4 h-4 mr-2"/>Download
      </Button>
    </div>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const [digestBusy, setDigestBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previews, setPreviews] = useState(null); // array of preview payloads (1 or N)
  const [previewIdx, setPreviewIdx] = useState(0);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState(ALL_STORES);

  useEffect(() => {
    if (user.role !== "admin") return;
    (async () => {
      try {
        const { data } = await api.get("/stores");
        setStores(data);
      } catch (e) { console.warn("failed to load report stores", e); }
    })();
  }, [user.role]);

  // Resolve which store_id to send with each report request.
  const resolveStoreId = () => {
    if (user.role !== "admin") return user.store_id;
    return storeFilter === ALL_STORES ? undefined : storeFilter;
  };
  const storeSuffix = () => {
    if (user.role !== "admin") return "";
    if (storeFilter === ALL_STORES) return "";
    const s = stores.find(x => x.id === storeFilter);
    return s ? `-${s.code}` : "";
  };

  const dl = async (path, baseName, ext) => {
    const storeId = resolveStoreId();
    const params = storeId ? { store_id: storeId } : {};
    const { data } = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}${storeSuffix()}.${ext}`;
    a.click();
  };

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/reports/send-weekly-digest");
      const emailed = (data.results || []).filter(r => r.email_sent).length;
      toast.success(`Summary ran for ${data.stores} store${data.stores === 1 ? "" : "s"}${emailed ? ` · ${emailed} email(s) sent` : " · in-app only (no email delivery)"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Summary failed");
    } finally { setDigestBusy(false); }
  };

  const previewDigest = async () => {
    setPreviewBusy(true);
    try {
      if (storeFilter === ALL_STORES) {
        const { data } = await api.get("/reports/digest-preview-all");
        if (!data.previews?.length) { toast.error("No active stores to preview"); return; }
        setPreviews(data.previews);
        setPreviewIdx(0);
      } else {
        const { data } = await api.get("/reports/digest-preview", { params: { store_id: storeFilter } });
        setPreviews([data]);
        setPreviewIdx(0);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewBusy(false); }
  };

  const preview = previews ? previews[previewIdx] : null;
  const totalPreviews = previews?.length || 0;
  const goPrev = () => setPreviewIdx((i) => (i - 1 + totalPreviews) % totalPreviews);
  const goNext = () => setPreviewIdx((i) => (i + 1) % totalPreviews);

  const currentStoreName = () => {
    if (user.role !== "admin") return "";
    if (storeFilter === ALL_STORES) return "All stores";
    return stores.find(s => s.id === storeFilter)?.name || "Selected store";
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="overline">Insights</div><h1 className="font-display text-4xl sm:text-5xl mt-2">Reports</h1></div>
        {user.role === "admin" && (
          <Button variant="outline" onClick={sendDigest} disabled={digestBusy} data-testid="send-weekly-digest-btn">
            {digestBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Mail className="w-4 h-4 mr-2"/>}
            Send weekly summary now
          </Button>
        )}
      </div>

      {user.role === "admin" && (
        <div className="grid-border-card p-4 flex flex-wrap gap-3 items-end" data-testid="reports-store-picker-card">
          <div>
            <div className="overline mb-1.5">Scope reports to</div>
            <Select value={storeFilter} onValueChange={setStoreFilter}>
              <SelectTrigger className="w-72" data-testid="reports-store-picker">
                <SelectValue placeholder="All stores"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STORES}>All stores</SelectItem>
                {stores.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name} · {s.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={previewDigest}
            disabled={previewBusy}
            data-testid="preview-digest-btn"
            title={storeFilter === ALL_STORES ? "Preview weekly summary for every active store (paged)" : "Preview weekly summary for this store"}
          >
            {previewBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Eye className="w-4 h-4 mr-2"/>}
            Preview summary{storeFilter === ALL_STORES ? " · all stores" : ""}
          </Button>
          <div className="text-xs text-muted-foreground pb-1 ml-auto">
            Downloads below will be filtered to <span className="font-mono">{currentStoreName()}</span>.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4">
        <ReportCard
          title="Current inventory · CSV"
          description="Spreadsheet snapshot of stock levels with SKU, title, store, and low-stock flag."
          format="CSV"
          onDownload={() => dl("/reports/inventory", "inventory", "csv")}
          testid="report-inventory-btn"
        />
        <ReportCard
          title="Current inventory · PDF"
          description="Printable, branded summary of every book across your stores — great for meetings and audits."
          format="PDF"
          onDownload={() => dl("/reports/inventory.pdf", "inventory", "pdf")}
          testid="report-inventory-pdf-btn"
        />
        {user.role === "admin" && (
          <>
            <ReportCard
              title="Inventory movements · CSV"
              description="Every add / subtract event: received, sold, damaged, lost, transferred."
              format="CSV"
              onDownload={() => dl("/reports/transactions", "movements", "csv")}
              testid="report-transactions-btn"
            />
            <ReportCard
              title="Inventory movements · PDF"
              description="Chronological printable ledger of every stock movement — sign-off friendly."
              format="PDF"
              onDownload={() => dl("/reports/transactions.pdf", "movements", "pdf")}
              testid="report-transactions-pdf-btn"
            />
          </>
        )}
      </div>

      <Dialog open={!!previews} onOpenChange={(o) => { if (!o) { setPreviews(null); setPreviewIdx(0); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3 flex-wrap">
              <span>Weekly summary preview</span>
              {totalPreviews > 1 && (
                <span className="text-xs font-normal font-mono text-muted-foreground" data-testid="digest-preview-counter">
                  {previewIdx + 1} of {totalPreviews}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Read-only preview of the exact email the weekly summary will send.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="flex flex-col gap-3 min-h-0">
              <div className="text-xs grid grid-cols-2 md:grid-cols-4 gap-3 pb-3 border-b border-border">
                <div><div className="overline">Store</div><div className="font-mono truncate">{preview.store_name}</div></div>
                <div><div className="overline">To</div><div className="font-mono truncate">{preview.manager_email || "— no manager email —"}</div></div>
                <div><div className="overline">Subject</div><div className="font-mono truncate">{preview.subject}</div></div>
                <div><div className="overline">Stats</div><div className="font-mono">{preview.units_moved} moved · {preview.low_stock_count} low</div></div>
              </div>
              <iframe
                title="digest preview"
                data-testid="digest-preview-iframe"
                srcDoc={`<!doctype html><html><body style='margin:0;background:#faf7f2;padding:16px'>${preview.html}</body></html>`}
                className="w-full h-[58vh] rounded-sm border border-border bg-[#faf7f2]"
                sandbox=""
              />
              <div className="flex items-center gap-2 flex-wrap">
                {totalPreviews > 1 && (
                  <>
                    <Button variant="outline" size="sm" onClick={goPrev} data-testid="digest-preview-prev">
                      <ChevronLeft className="w-4 h-4 mr-1"/>Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={goNext} data-testid="digest-preview-next">
                      Next<ChevronRight className="w-4 h-4 ml-1"/>
                    </Button>
                    <div className="text-xs text-muted-foreground font-mono">
                      {preview.store_code} · {preview.store_name}
                    </div>
                  </>
                )}
                <div className="text-xs text-muted-foreground ml-auto">
                  Nothing sent — read-only render.
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
